import { useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import {
  Camera, UserRound, FileText, IdCard, CheckCircle2, Upload, Save, ExternalLink,
} from "lucide-react";

function DocRow({ docType, icon: Icon, label, inputRef, uploaded, onUpload }) {
  return (
    <div className="border border-border p-5 flex items-center gap-4" data-testid={`doc-row-${docType}`}>
      <div className={`w-11 h-11 flex items-center justify-center shrink-0 ${uploaded ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground"}`}>
        <Icon size={20} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{label}</p>
        {uploaded ? (
          <a
            href={fileUrl(uploaded.path)} target="_blank" rel="noreferrer"
            data-testid={`doc-view-${docType}`}
            className="text-xs text-primary font-mono inline-flex items-center gap-1 truncate"
          >
            {uploaded.filename} <ExternalLink size={11} />
          </a>
        ) : (
          <p className="text-xs text-muted-foreground">Not uploaded yet</p>
        )}
      </div>
      {uploaded && <CheckCircle2 className="text-primary shrink-0" size={18} />}
      <button
        onClick={() => inputRef.current?.click()}
        data-testid={`doc-upload-${docType}`}
        className="px-3 py-2 text-sm font-medium border border-border hover:border-primary inline-flex items-center gap-2 shrink-0 transition-colors duration-150"
      >
        <Upload size={14} /> {uploaded ? "Replace" : "Upload"}
      </button>
      <input
        ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden"
        onChange={(e) => onUpload(docType, e)}
      />
    </div>
  );
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", phone: user?.phone || "",
    institution: user?.institution || "", student_number: user?.student_number || "",
  });
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState(user?.documents || {});
  const [photo, setPhoto] = useState(user?.photo_path || null);
  const photoRef = useRef();
  const porRef = useRef();
  const idRef = useRef();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/profile", form);
      await refresh();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto(data.path);
      await refresh();
      toast.success("Photo updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const uploadDoc = async (docType, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("doc_type", docType);
    fd.append("file", file);
    try {
      const { data } = await api.post("/profile/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs(data.documents);
      await refresh();
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const field = (label, key, placeholder = "") => (
    <div>
      <label className="block text-sm font-medium mb-1.5">{label}</label>
      <input
        value={form[key]} onChange={set(key)} placeholder={placeholder}
        data-testid={`profile-${key}-input`}
        className="w-full bg-secondary border border-border px-3.5 py-3 text-sm outline-none focus:ring-2 focus:ring-primary focus:border-primary"
      />
    </div>
  );

  const DocRowLegacyRemoved = null;

  return (
    <div>
      <p className="text-xs font-medium tracking-overline text-primary uppercase mb-2">Account</p>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-8">Student profile</h1>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Photo + identity */}
        <div className="border border-border p-6 flex flex-col items-center text-center" data-testid="profile-photo-card">
          <div className="relative">
            <div className="w-28 h-28 overflow-hidden bg-secondary flex items-center justify-center">
              {photo ? (
                <img src={fileUrl(photo)} alt="Profile" className="w-full h-full object-cover" data-testid="profile-photo-img" />
              ) : (
                <UserRound size={44} className="text-muted-foreground" />
              )}
            </div>
            <button
              onClick={() => photoRef.current?.click()}
              data-testid="photo-upload-btn"
              className="absolute -bottom-2 -right-2 w-9 h-9 bg-primary text-white flex items-center justify-center hover:-translate-y-0.5 transition-transform duration-150"
            >
              <Camera size={16} />
            </button>
            <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
          </div>
          <p className="mt-4 font-bold">{user?.name}</p>
          <p className="text-xs text-muted-foreground">{user?.email}</p>
          {user?.institution && (
            <p className="mt-3 text-xs font-medium tracking-overline uppercase text-primary">{user.institution}</p>
          )}
          <div className="mt-5 w-full border-t border-border pt-4 grid grid-cols-2 gap-3 text-left">
            <div>
              <p className="text-xs text-muted-foreground">Points</p>
              <p className="font-mono font-bold text-primary">{(user?.points ?? 0).toLocaleString("en-ZA")}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Student no.</p>
              <p className="font-mono font-bold truncate">{user?.student_number || "—"}</p>
            </div>
          </div>
        </div>

        {/* Details form */}
        <div className="lg:col-span-2 border border-border p-6" data-testid="profile-details-card">
          <h2 className="font-bold mb-5">Personal details</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {field("Full name", "name")}
            {field("Phone", "phone", "+27 …")}
            {field("Institution", "institution", "University / TVET")}
            {field("Student number", "student_number")}
          </div>
          <button
            onClick={saveProfile} disabled={saving}
            data-testid="profile-save-btn"
            className="mt-6 bg-primary text-white font-medium px-6 py-3 inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform duration-150 disabled:opacity-60"
          >
            <Save size={16} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      {/* Verification docs */}
      <div className="mt-8">
        <h2 className="font-bold mb-2">Verification documents</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Upload your proof of registration and a copy of your ID to verify your student status.
        </p>
        <div className="grid gap-4">
          <DocRow docType="proof_of_registration" icon={FileText} label="Proof of registration" inputRef={porRef} uploaded={docs?.proof_of_registration} onUpload={uploadDoc} />
          <DocRow docType="id_copy" icon={IdCard} label="ID copy" inputRef={idRef} uploaded={docs?.id_copy} onUpload={uploadDoc} />
        </div>
      </div>
    </div>
  );
}
